import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PlaceholderCard } from "../components/placeholder-card";
import { KeywordsTable } from "../components/keywords-table";
import { StatsCards } from "../components/stats-cards";
import { TimeSeriesChart } from "../components/time-series-chart";
import { TopPagesCard } from "../components/top-pages-card";
import { BacklinkDashboardService } from "../services";
import {
  IBacklinkImpressions,
  IBacklinkKeywords,
  IBacklinkSessions,
  IBacklinkTimeSeries,
  IBacklinkTopPages,
} from "../types";

export function BacklinksDashboardPage() {
  const [searchParams] = useSearchParams();
  const customerId =
    searchParams.get("customerId") ?? searchParams.get("id") ?? undefined;

  const [loading, setLoading] = useState(false);
  const [impressions, setImpressions] = useState<IBacklinkImpressions | null>(null);
  const [sessions, setSessions] = useState<IBacklinkSessions | null>(null);
  const [timeSeries, setTimeSeries] = useState<IBacklinkTimeSeries | null>(null);
  const [keywords, setKeywords] = useState<IBacklinkKeywords | null>(null);
  const [topPages, setTopPages] = useState<IBacklinkTopPages | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setFetchFailed(false);

    Promise.all([
      BacklinkDashboardService.getImpressions(customerId),
      BacklinkDashboardService.getSessions(customerId),
      BacklinkDashboardService.getTimeSeries(customerId),
      BacklinkDashboardService.getKeywords(customerId),
      BacklinkDashboardService.getTopPages(customerId),
      BacklinkDashboardService.getPostsCount(customerId),
    ])
      .then(([imp, sess, ts, kw, pages]) => {
        setImpressions(imp);
        setSessions(sess);
        setTimeSeries(ts);
        setKeywords(kw);
        setTopPages(pages);
        if (!imp && !sess && !ts && !kw && !pages) setFetchFailed(true);
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (fetchFailed) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <Typography color="text.secondary">
          Não foi possível carregar o dashboard. Verifique se há cliente vinculado ao usuário.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Dashboard
      </Typography>

      <Box mb={3}>
        <StatsCards impressions={impressions} sessions={sessions} />
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid size={8}>
          <TimeSeriesChart data={timeSeries?.timeSeries ?? []} />
        </Grid>
        <Grid size={4}>
          <PlaceholderCard />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={6}>
          <KeywordsTable keywords={keywords?.topKeywords ?? []} />
        </Grid>
        <Grid size={6}>
          <TopPagesCard pages={topPages?.topPages ?? []} />
        </Grid>
      </Grid>
    </Box>
  );
}
